import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Fix AI (2026-09-04): "cuando le demos atras en el navegador vaya a la ventana
// que estaba abierta anteriormente, ahora mismo (...) va a la ventana de Obras
// por defecto" (Edson, 04/09).
//
// Un modal abierto no existe para el navegador: "atrás" sale de la página y
// pierde todo lo que había en pantalla. Este hook le agrega al historial una
// marca propia mientras el modal está abierto (misma URL, no navega a ningún
// lado): si el usuario da "atrás", cerramos el modal en vez de salir.
//
// Fix AP (2026-09-05): se mira el índice de historial del router (no un id), así
// un modal que se abre ENCIMA de otro no cierra al de abajo.
//
// Fix AR (2026-09-05): en dispositivos táctiles el hook queda INERTE. El
// navegador del celular deshacía la entrada apenas se agregaba y el modal
// "parpadeaba" sin abrir.
//
// Fix AT (2026-09-05): la marca también se saca al DESMONTARSE el modal. La
// mayoría de los modales se desmontan al cerrarse (`if (!open) return null`), así
// que la rama de cierre del efecto no llegaba a correr y cada ventana abierta y
// cerrada dejaba una entrada muerta en el historial: después de mirar cinco
// pedidos había que apretar "atrás" cinco veces sin ver ningún cambio.
const MS_GRACIA = 600;

const esTactil = () => {
  try {
    return typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
};

export function useCerrarConAtras(open, onClose) {
  const navigate = useNavigate();
  const location = useLocation();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const tactilRef = useRef(null);   // se evalúa una sola vez
  if (tactilRef.current === null) tactilRef.current = esTactil();
  const idxRef = useRef(null);      // índice de historial de NUESTRA marca
  const desdeRef = useRef(0);       // cuándo se registró la marca
  const abiertoRef = useRef(false);

  const idxActual = () => {
    try {
      const s = window.history.state;
      return s && typeof s.idx === "number" ? s.idx : null;
    } catch {
      return null;
    }
  };

  // Saca NUESTRA marca del historial, pero solo si sigue siendo la última: si el
  // usuario navegó a otra pantalla o hay otro modal encima, no se toca nada.
  const soltarMarca = () => {
    const mio = idxRef.current;
    idxRef.current = null;
    abiertoRef.current = false;
    try {
      if (mio != null && idxActual() === mio) navigateRef.current(-1);
    } catch {
      /* la app se está desmontando: no hay nada que hacer */
    }
  };

  useEffect(() => {
    if (tactilRef.current) return;   // Fix AR: en el celular no se toca el historial
    if (open && !abiertoRef.current) {
      abiertoRef.current = true;
      idxRef.current = null;  // se registra en el efecto de abajo, tras el push
      desdeRef.current = Date.now();
      navigate(location.pathname + location.search, { state: { ...(location.state || {}), cenModal: true } });
    } else if (!open && abiertoRef.current) {
      // Cerrado con la X / Escape / un botón, con el componente todavía montado.
      soltarMarca();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fix AT (2026-09-05): el mismo cierre, cuando el modal se desmonta.
  useEffect(() => () => {
    if (!tactilRef.current && abiertoRef.current) soltarMarca();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tactilRef.current) return;   // Fix AR
    if (!abiertoRef.current) return;
    const i = idxActual();
    if (i == null) return;                       // sin índice: no cerramos nada
    if (idxRef.current == null) { idxRef.current = i; return; }  // nuestra marca
    if (i < idxRef.current) {
      if (Date.now() - desdeRef.current < MS_GRACIA) {
        // Retroceso que no pidió el usuario: el modal se queda abierto.
        idxRef.current = null;
        return;
      }
      idxRef.current = null;
      abiertoRef.current = false;
      onCloseRef.current?.();
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps
}
